import { useCallback, useEffect, useRef, useState } from "react";
import { logApplicationFailure } from "@/lib/user-error";

const COPIED_FOR_MS = 1600;

/**
 * Kopiert einen Text in die Zwischenablage und meldet das kurz als `copied`.
 *
 * Ohne sicheren Kontext (http auf einem anderen Host als localhost) gibt es keine Clipboard-API. Ein
 * Optional-Chain schluckte das und meldete Erfolg, obwohl nichts kopiert wurde; deshalb zählt hier
 * nur ein wirklich geschriebener Text als kopiert, und das Scheitern geht unter `failureAction` in die
 * Diagnose.
 */
export function useCopyText(failureAction: string): { copied: boolean; copy: (text: string) => Promise<void> } {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string) => {
      try {
        if (!navigator.clipboard) throw new Error("clipboard unavailable");
        await navigator.clipboard.writeText(text);
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), COPIED_FOR_MS);
      } catch {
        logApplicationFailure(failureAction);
      }
    },
    [failureAction],
  );

  return { copied, copy };
}
